import React, { useState } from 'react';
import {
  Sparkles,
  Loader2,
  Info,
  Clock,
  User
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useOrganizationSettings, OrganizationFeature } from '../../hooks/useOrganizationSettings';
import ConfirmationDialog from '../ConfirmationDialog';

interface Props {
  organizationId: string;
}

const AIFeaturesTab: React.FC<Props> = ({ organizationId }) => {
  const { 
    featuresByCategory, 
    loading, 
    error, 
    updateFeature 
  } = useOrganizationSettings(organizationId);
  
  const [pendingToggle, setPendingToggle] = useState<{
    feature: OrganizationFeature;
    newValue: boolean;
  } | null>(null);
  const [updating, setUpdating] = useState<string | null>(null);

  const handleToggle = async (feature: OrganizationFeature, newValue: boolean) => {
    // If disabling, show confirmation dialog
    if (!newValue) {
      setPendingToggle({ feature, newValue });
      return;
    }
    
    await executeToggle(feature.feature_key, newValue);
  };

  const executeToggle = async (featureKey: string, newValue: boolean) => {
    try {
      setUpdating(featureKey);
      await updateFeature(featureKey, newValue);
      toast.success(newValue ? 'Feature enabled' : 'Feature disabled');
    } catch (err) {
      console.error('Failed to update feature:', err);
      toast.error('Failed to update feature');
    } finally {
      setUpdating(null);
      setPendingToggle(null);
    }
  };

  const confirmToggle = () => {
    if (pendingToggle) {
      executeToggle(pendingToggle.feature.feature_key, pendingToggle.newValue);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-error-600 dark:text-error-400">{error}</p>
      </div>
    );
  }

  const aiFeatures = featuresByCategory['AI Features'] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
          AI Features
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
          Control AI-generated content displayed throughout your organization
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
        <div className="flex gap-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-1">
              About AI Summaries
            </h4>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              AI summaries are automatically generated for legislation to help your team quickly understand 
              bills. These summaries include brief overviews, detailed analyses, and balanced perspectives 
              on supporting and opposing arguments.
            </p>
          </div>
        </div>
      </div>

      {/* AI Features List */}
      {aiFeatures.length === 0 ? (
        <div className="card p-6 text-center">
          <Sparkles className="w-12 h-12 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
          <p className="text-text-secondary dark:text-text-dark-secondary">
            No AI features are available for configuration.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {aiFeatures.map((feature) => (
            <div 
              key={feature.feature_key}
              className="card p-4"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-primary-500" />
                    <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                      {feature.feature_name}
                    </h4>
                  </div>
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                    {feature.description}
                  </p>
                  
                  {/* Last modified info */}
                  {feature.modified_at && (
                    <div className="flex items-center gap-4 mt-2 text-xs text-text-tertiary dark:text-text-dark-tertiary">
                      {feature.modified_by && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {feature.modified_by}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(feature.modified_at).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>

                {/* Toggle Switch */}
                <div className="flex-shrink-0">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={feature.is_enabled}
                    disabled={updating === feature.feature_key}
                    onClick={() => handleToggle(feature, !feature.is_enabled)}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                      ${feature.is_enabled 
                        ? 'bg-primary-600' 
                        : 'bg-background-tertiary dark:bg-background-dark-quaternary'
                      }
                      ${updating === feature.feature_key ? 'opacity-50 cursor-wait' : ''}
                    `}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                        transition duration-200 ease-in-out
                        ${feature.is_enabled ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    >
                      {updating === feature.feature_key && (
                        <Loader2 className="w-3 h-3 animate-spin absolute top-1 left-1 text-primary-500" />
                      )}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Other Categories (future-proofing).
          budget_tracking is hidden: the Budget module is gated by the
          `budget_enabled` LaunchDarkly flag, so an org-level toggle here was a
          second, contradictory switch for the same feature (issue #1137). The
          feature row still exists server-side — only the control is withdrawn.
          Categories left empty by the filter are dropped, so no bare heading. */}
      {Object.entries(featuresByCategory)
        .filter(([category]) => category !== 'AI Features')
        .map(([category, features]) => [
          category,
          features.filter((f) => f.feature_key !== 'budget_tracking'),
        ] as const)
        .filter(([, features]) => features.length > 0)
        .map(([category, features]) => (
          <div key={category} className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
              {category}
            </h3>
            {features.map((feature) => (
              <div 
                key={feature.feature_key}
                className="card p-4"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-text-primary dark:text-text-dark-primary">
                      {feature.feature_name}
                    </h4>
                    <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                      {feature.description}
                    </p>
                  </div>

                  <button
                    type="button"
                    role="switch"
                    aria-checked={feature.is_enabled}
                    disabled={updating === feature.feature_key}
                    onClick={() => handleToggle(feature, !feature.is_enabled)}
                    className={`
                      relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                      transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
                      ${feature.is_enabled 
                        ? 'bg-primary-600' 
                        : 'bg-background-tertiary dark:bg-background-dark-quaternary'
                      }
                      ${updating === feature.feature_key ? 'opacity-50 cursor-wait' : ''}
                    `}
                  >
                    <span
                      className={`
                        pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                        transition duration-200 ease-in-out
                        ${feature.is_enabled ? 'translate-x-5' : 'translate-x-0'}
                      `}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={pendingToggle !== null}
        title="Disable AI Feature"
        message={`Deactivating "${pendingToggle?.feature.feature_name}" will remove this AI summary type from all displays and reports throughout your organization. Are you sure you want to proceed?`}
        confirmText="Disable Feature"
        cancelText="Cancel"
        variant="warning"
        onConfirm={confirmToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </div>
  );
};

export default AIFeaturesTab;
